import axios from "axios";
import { toast } from "./components/ui/Toast";
import { addApiErrorBreadcrumb } from "./sentry";

const getBaseURL = () => {
  if (process.env.VITE_API_URL) {
    return process.env.VITE_API_URL;
  }
  if (process.env.NODE_ENV === "development") {
    return "http://localhost:8000";
  }
  return "";
};

const api = axios.create({
  baseURL: `${getBaseURL()}/api/v1`,
  withCredentials: true, // US230: send cookies (including httpOnly JWT) automatically
});

// US230: Add CSRF token to state-changing requests.
// The CSRF token is stored in a non-httpOnly cookie (pmm_csrf) that is
// readable by JavaScript. We read it and send it as the X-CSRF-Token header.
const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
};

api.interceptors.request.use((config: any) => {
  // Only add CSRF token for state-changing methods
  if (
    config.method &&
    ["post", "put", "patch", "delete"].includes(config.method)
  ) {
    const csrfToken = getCookie("pmm_csrf");
    if (csrfToken) {
      config.headers = config.headers || {};
      config.headers["X-CSRF-Token"] = csrfToken;
    }
  }
  return config;
});

export const getErrorMessage = (
  error: any,
  fallback = "An unexpected error occurred.",
): string => {
  return (
    error?.response?.data?.error?.message ||
    error?.response?.data?.detail ||
    fallback
  );
};

/**
 * Determine whether a 401 response came from an auth-related endpoint.
 * Auth endpoints are those under /auth/ (login, register, logout, etc.).
 * These should still hard-redirect because the user needs to re-authenticate.
 */
const isAuthEndpoint = (url?: string): boolean => {
  if (!url) return false;
  return url.includes("/auth/");
};

/**
 * Handle 401 responses from the API.
 *
 * US230: the JWT is now stored in an httpOnly cookie, not localStorage.
 * When a 401 occurs, the cookie has already expired or been cleared by
 * the backend. We show a toast and redirect to /login so the user can
 * re-authenticate.
 *
 * We skip the redirect for the initial /users/profile call during
 * AuthContext init, since that's expected when there's no session.
 */
export const handle401Error = (error: any): void => {
  const requestUrl = error?.config?.url || error?.config?.baseURL || "";

  // Don't redirect for the initial profile check — AuthContext init
  // handles this gracefully by setting user to null.
  if (requestUrl.includes("/users/profile")) {
    return;
  }

  toast.error("Your session has expired. Please log in again.");

  // Hard-redirect to login
  window.location.href = "/login";
};

/**
 * Handle 403 responses from the API.
 *
 * When the backend returns 403 with X-Error-Code: email_unverified,
 * redirect the user to the email verification page instead of login.
 * Guest users are exempt from verification, so this path only fires
 * for registered accounts that have not yet verified their email.
 */
export const handle403Error = (error: any): void => {
  const errorCode = error?.response?.headers?.["x-error-code"];
  if (errorCode === "email_unverified") {
    // Don't hard-redirect here — the Login page catches this same
    // error and navigates to /verify-email?email=... so the email
    // field is pre-filled. A hard redirect would lose the email.
    return;
  }
  // For other 403s (tier limits, etc.), let the component handle it.
};

// Global response interceptor for auth errors
api.interceptors.response.use(
  (response: any) => response,
  (error: any) => {
    // US237: capture 5xx errors as Sentry breadcrumbs
    if (error.response?.status && error.response.status >= 500) {
      addApiErrorBreadcrumb(
        error.config?.url || "unknown",
        error.response.status,
        error.config?.method || "get",
      );
    }
    if (error.response?.status === 401) {
      handle401Error(error);
    }
    if (error.response?.status === 403) {
      handle403Error(error);
    }
    return Promise.reject(error);
  },
);

export default api;
